import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Building2, Footprints, Accessibility, Wrench, RefreshCw, PlusSquare } from "lucide-react";
import { Eyebrow } from "@/components/landing/SiteLayout";
import { Reveal } from "@/components/landing/Reveal";
import { DecorHatch, DecorShaft } from "@/components/landing/Decor";



const KATEGORIEN = [
  { t: "Personenaufzug",     d: "Für Wohnhäuser, Geschäftsliegenschaften und Neubauprojekte.", Icon: Building2, href: "/personenaufzug-schweiz" },
  { t: "Homelift",           d: "Kompakte Lösungen für Einfamilienhäuser und private Objekte.", Icon: Home, href: "/homelift-schweiz" },
  { t: "Treppenlift",        d: "Barrierefreie Erschliessung für Innen- und Aussenbereiche.", Icon: Footprints, href: "/treppenlift-schweiz" },
  { t: "Plattformlift",      d: "Für Gewerbe, öffentliche Gebäude und barrierefreie Zugänge.", Icon: Accessibility, href: "/plattformlift-schweiz" },
  { t: "Wartung & Service",  d: "Serviceverträge, Anbieterwechsel und laufende Betreuung.", Icon: Wrench, href: "/liftservice-schweiz" },
  { t: "Modernisierung",     d: "Erneuerung von Kabine, Steuerung, Antrieb und Sicherheit.", Icon: RefreshCw, href: "/liftmodernisierung-schweiz" },
  { t: "Lift nachrüsten",    d: "Nachträglicher Einbau inklusive Machbarkeitsabklärung.", Icon: PlusSquare, href: "/lift-nachtraeglich-einbauen" },
];

export function LiftartenSection({ asPageHeading = false }: { asPageHeading?: boolean } = {}) {
  const navigate = useNavigate();
  const Heading = asPageHeading ? "h1" : "h2";
  return (
    <section id="liftarten" className="relative overflow-hidden py-14 sm:py-20 scroll-mt-16">
      <DecorHatch className="right-0 top-24 h-[420px] w-[24%]" fade="left" />
      <DecorShaft className="hidden lg:block left-[4%] top-0 bottom-0 w-[80px] opacity-70" />
      <div className="relative max-w-7xl mx-auto px-6">

        <div className="max-w-2xl mb-10 sm:mb-12 mx-auto">
          <Eyebrow>Anfragekategorien</Eyebrow>
          <Heading className="mt-6 text-3xl sm:text-[44px] font-semibold tracking-[-0.03em] text-foreground leading-[1.1]">
            Welche Liftprojekte können Sie erfassen?
          </Heading>
          <p className="mt-6 text-[16px] sm:text-[17px] text-muted-foreground leading-[1.7]">
            Von Neubau über Modernisierung bis Wartung: Liftblick strukturiert Ihre Anfrage so, dass passende Anbieter Ihr Projekt schnell einschätzen können.
          </p>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {KATEGORIEN.map(({ t, d, Icon, href }, i) => (
            <Reveal as="li" key={t} delay={(i % 3) * 70} className="h-full">
              <div className="group relative h-full rounded-3xl bg-[#F7F8FA] p-6 sm:p-7 transition-all duration-200 hover:bg-background hover:shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_28px_-14px_rgba(15,23,42,0.18)] hover:-translate-y-0.5">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-background text-foreground/80 transition-colors duration-200 group-hover:text-foreground">
                  <Icon className="h-[22px] w-[22px]" strokeWidth={1.6} />
                </div>
                <h3 className="mt-6 text-[17px] font-semibold text-foreground tracking-[-0.01em]">
                  <a href={href} className="text-foreground no-underline before:absolute before:inset-0 before:content-['']">
                    {t}
                  </a>
                </h3>
                <p className="mt-2.5 text-[14px] text-muted-foreground leading-[1.7]">{d}</p>
              </div>
            </Reveal>
          ))}
        </ul>

        <Reveal className="mt-6">
          <div className="relative overflow-hidden flex flex-col items-center rounded-3xl bg-foreground px-6 sm:px-10 py-9 sm:py-11 text-center">
            <DecorHatch className="left-0 top-0 h-full w-[34%] deco-hatch-light" fade="right" />
            <DecorShaft className="hidden sm:block left-[8%] top-0 bottom-0 w-[70px]" light />
            <DecorHatch className="right-0 top-0 h-full w-[34%] deco-hatch-light" fade="left" />
            <p className="relative w-full text-center text-[19px] sm:text-[23px] font-semibold tracking-[-0.02em] text-background leading-[1.3]">
              Nicht sicher, welche Kategorie passt?
            </p>
            <p className="relative mt-3 max-w-md text-center text-[14.5px] leading-[1.7] text-background/70">
              Beschreiben Sie Ihr Vorhaben einfach in eigenen Worten. Wir strukturieren die Anfrage für passende Anbieter.
            </p>
            <Button
              onClick={() => navigate("/wizard")}
              size="lg"
              variant="secondary"
              className="relative mt-7 w-full sm:w-auto min-h-[52px] rounded-full px-8 text-[15.5px] font-semibold transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
            >

              Liftprojekt kostenlos erfassen
            </Button>
            <p className="relative mt-4 w-full text-center text-[13px] text-background/60">
              Kostenlos - unverbindlich - Anbieter melden sich direkt bei Ihnen
            </p>
          </div>
        </Reveal>


      </div>
    </section>
  );
}
